import { useState } from "react"
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts"
import { calculateProjection } from "@/lib/projection-math"
import type { AssetProjection, ContributionStep } from "@/lib/projection-math"
import type { FireSettings } from "@/types/fire"
import { useCurrency } from "@/context/currency/currency-context"
import { cn } from "@/lib/utils"

interface FireScenarioChartProps {
	assets: AssetProjection[]
	settings: FireSettings
	contribution: number
	contributionSchedule?: ContributionStep[]
	annualGoal: number
	freedomYear: number | null
}

export function FireScenarioChart({
	assets,
	settings,
	contribution,
	contributionSchedule,
	annualGoal,
	freedomYear: _freedomYear,
}: FireScenarioChartProps) {
	const { format, convert } = useCurrency()
	const [visible, setVisible] = useState({ conservative: true, base: true, optimistic: true, portfolioValue: true })

	const convertedGoal = annualGoal // pre-converted to display currency in FirePage
	const cap = settings.dividend_growth_cap

	// Derive scenario params from settings (with fallback defaults)
	const conserv = settings.scenario_overrides?.conservative
	const optim = settings.scenario_overrides?.optimistic

	const SCENARIOS = [
		{
			key: "conservative" as const,
			label: "Conservative",
			color: "#f59e0b",
			priceAppreciation: conserv?.price_appreciation ?? 2,
			divGrowth: conserv?.expected_dividend_growth ?? 3,
		},
		{
			key: "base" as const,
			label: "Base",
			color: "#0ea5e9",
			priceAppreciation: settings.price_appreciation,
			// Keep per-position historical rates, but flatten after 5 years
			divGrowth: null as number | null,
		},
		{
			key: "optimistic" as const,
			label: "Optimistic",
			color: "#22c55e",
			priceAppreciation: optim?.price_appreciation ?? 8,
			divGrowth: optim?.expected_dividend_growth ?? 7,
		},
	]

	// Run all 3 projections
	const projections = SCENARIOS.map((scenario) => {
		const scenarioAssets: AssetProjection[] = assets.map((a) => ({
			...a,
			dividendGrowth: scenario.divGrowth !== null ? scenario.divGrowth : a.dividendGrowth,
		}))
		return calculateProjection({
			assets: scenarioAssets,
			contribution,
			frequency: "Monthly",
			drip: settings.drip_enabled,
			priceAppreciation: scenario.priceAppreciation ?? settings.price_appreciation,
			dividendGrowthCap: cap,
			incomeGoal: convert(settings.monthly_expense_target, settings.currency),
			years: 50,
			contributionSchedule,
			// Base uses per-position historical rates for the first 5 years,
			// then settles to the income-weighted average rate
			flattenAfterYear: scenario.divGrowth === null ? 5 : undefined,
		})
	})

	// Always show 30 years (matches main projection chart)
	const currentYear = new Date().getFullYear()
	const endYear = currentYear + 30

	// Merge into single chart data array
	const baseData = projections[1].data.filter((d) => d.year <= endYear)
	const chartData = baseData.map((d) => {
		const year = d.year
		return {
			year,
			conservative: projections[0].data.find((x) => x.year === year)?.annualIncome ?? 0,
			base: projections[1].data.find((x) => x.year === year)?.annualIncome ?? 0,
			optimistic: projections[2].data.find((x) => x.year === year)?.annualIncome ?? 0,
			pv_conservative: projections[0].data.find((x) => x.year === year)?.portfolioValue ?? 0,
			pv_base: projections[1].data.find((x) => x.year === year)?.portfolioValue ?? 0,
			pv_optimistic: projections[2].data.find((x) => x.year === year)?.portfolioValue ?? 0,
		}
	})

	// Left Y axis (income) — nice ticks
	const TICK_COUNT = 4
	const allIncomeValues = chartData.flatMap((d) => [
		visible.conservative ? d.conservative : 0,
		visible.base ? d.base : 0,
		visible.optimistic ? d.optimistic : 0,
	])
	const incomeMax = Math.max(...allIncomeValues, convertedGoal, 1)
	const rawStep = incomeMax / TICK_COUNT
	const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
	const niceStep = Math.ceil(rawStep / magnitude) * magnitude
	const niceMax = niceStep * TICK_COUNT
	const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => i * niceStep)

	// Right Y axis (portfolio value) — nice ticks
	const allPortfolioValues = visible.portfolioValue ? chartData.flatMap((d) => [
		visible.conservative ? d.pv_conservative : 0,
		visible.base ? d.pv_base : 0,
		visible.optimistic ? d.pv_optimistic : 0,
	]) : [0]
	const pvMax = Math.max(...allPortfolioValues, 1)
	const pvRawStep = pvMax / TICK_COUNT
	const pvMagnitude = Math.pow(10, Math.floor(Math.log10(pvRawStep)))
	const pvNiceStep = Math.ceil(pvRawStep / pvMagnitude) * pvMagnitude
	const pvNiceMax = pvNiceStep * TICK_COUNT
	const pvTicks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => i * pvNiceStep)

	const toggleScenario = (key: keyof typeof visible) => {
		setVisible((v) => ({ ...v, [key]: !v[key] }))
	}

	return (
		<div className="space-y-3">
			{/* Toggle chips */}
			<div className="flex flex-wrap gap-2">
				{SCENARIOS.map((s) => (
					<button
						key={s.key}
						onClick={() => toggleScenario(s.key)}
						className={cn(
							"px-3 py-1 rounded-full text-xs font-medium border transition-all",
							visible[s.key]
								? "text-white border-transparent"
								: "bg-transparent text-muted-foreground border-border",
						)}
						style={visible[s.key] ? { backgroundColor: s.color } : {}}
					>
						{s.label}
					</button>
				))}
				<button
					onClick={() => toggleScenario("portfolioValue")}
					className={cn(
						"px-3 py-1 rounded-full text-xs font-medium border transition-all",
						visible.portfolioValue
							? "bg-slate-600 text-white border-transparent"
							: "bg-transparent text-muted-foreground border-border",
					)}
				>
					Portfolio Value
				</button>
			</div>

			<div className="h-64">
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart tabIndex={-1} data={chartData} margin={{ top: 10, right: visible.portfolioValue ? 60 : 48, left: 0, bottom: 0 }}>
						<defs>
							{SCENARIOS.map((s) => (
								<linearGradient
									key={s.key}
									id={`scenarioGradient-${s.key}`}
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									<stop offset="5%" stopColor={s.color} stopOpacity={0.15} />
									<stop offset="95%" stopColor={s.color} stopOpacity={0} />
								</linearGradient>
							))}
						</defs>
						<CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
						<XAxis dataKey="year" tick={{ fontSize: 11 }} />
						<YAxis
							yAxisId="income"
							ticks={ticks}
							domain={[0, niceMax]}
							tickFormatter={(v) => format(v, { compact: true, skipConversion: true })}
							tick={{ fontSize: 11 }}
							width={60}
						/>
						{visible.portfolioValue && (
							<YAxis
								yAxisId="value"
								orientation="right"
								ticks={pvTicks}
								domain={[0, pvNiceMax]}
								tickFormatter={(v) => format(v, { compact: true, skipConversion: true })}
								tick={{ fontSize: 11 }}
								width={60}
							/>
						)}
						<Tooltip
							content={({ active, payload, label }) => {
								if (!active || !payload?.length) return null
								const vals: Record<string, number> = {}
								for (const entry of payload) {
									if (entry.dataKey && entry.value != null) {
										vals[entry.dataKey as string] = entry.value as number
									}
								}
								return (
									<div className="rounded-lg border bg-background p-3 shadow-md text-xs space-y-2">
										<p className="font-medium">Year {label}</p>
										{SCENARIOS.map((s) => {
											if (!visible[s.key]) return null
											const income = vals[s.key]
											const pv = vals[`pv_${s.key}`]
											if (income == null && pv == null) return null
											return (
												<div key={s.key}>
													<p className="font-medium" style={{ color: s.color }}>{s.label}:</p>
													{income != null && (
														<p className="pl-2 text-muted-foreground">Income: {format(income, { skipConversion: true })}</p>
													)}
													{visible.portfolioValue && pv != null && (
														<p className="pl-2 text-muted-foreground">Portfolio Value: {format(pv, { skipConversion: true })}</p>
													)}
												</div>
											)
										})}
									</div>
								)
							}}
						/>
						{annualGoal > 0 && (
							<ReferenceLine
								yAxisId="income"
								y={convertedGoal}
								stroke="#16a34a"
								strokeDasharray="6 3"
								label={{ value: "Goal", position: "right", fontSize: 11, fill: "#16a34a" }}
							/>
						)}
						{SCENARIOS.map((s) =>
							visible[s.key] ? (
								<Area
									key={s.key}
									yAxisId="income"
									type="monotone"
									dataKey={s.key}
									stroke={s.color}
									strokeWidth={2}
									fill={`url(#scenarioGradient-${s.key})`}
								/>
							) : null,
						)}
						{visible.portfolioValue &&
							SCENARIOS.map((s) =>
								visible[s.key] ? (
									<Line
										key={`pv_${s.key}`}
										yAxisId="value"
										type="monotone"
										dataKey={`pv_${s.key}`}
										stroke={s.color}
										strokeWidth={2}
										strokeDasharray="6 3"
										dot={false}
									/>
								) : null,
							)}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}
